import type { HtmlAttributes } from '@studio/design-system/props';
import type { Settings } from '@studio/models/control';
import type { Configuration } from '@studio/models/control/visca-ip';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn, useMount } from '@studio/react-utils/hooks';
import type { T_Nullable } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { shallow } from 'zustand/shallow';
import { useProducer, useStore } from '../store/hooks.js';
import { resolveViscaIpSettings } from './normalize.js';
import { ViscaIp as _ViscaIp } from '#~/control/components/editor/index.js';

/** @internal */
const Inner = (props: HtmlAttributes): ReactElement => {
  const producer = useProducer();

  const settings = useStore(
    ({ candidate }) =>
      // We know this is a normalized VISCA/IP configuration.
      candidate.control as Configuration,
    shallow
  );

  const handleChange = useLatestFn((value: Settings) => {
    producer((draft) => {
      draft.candidate.control = value;
    });
  });

  return <_ViscaIp settings={toAccessor(settings, handleChange)} advanced={false} {...props} />;
};

/** Visca/IP settings. */
export const ViscaIp = (props: HtmlAttributes): T_Nullable<ReactElement> => {
  const [isPrimed, setPrimed] = useState(false);
  const producer = useProducer();

  // On mount, normalize the Visca/IP control settings.
  useMount(() => {
    queueMicrotask(() => {
      producer((draft) => {
        const {
          candidate,
          manufacturer: { config }
        } = draft;

        const { control, video } = candidate;

        // Moved verbatim to normalize.ts so the first-run funnel resolves the
        // same endpoint without rendering this panel. See that file's header.
        draft.candidate.control = resolveViscaIpSettings(control, config, video);
      });

      setPrimed(true);
    });
  });

  return isPrimed ? <Inner {...props} /> : null;
};
